/**
 * Client enquiry detail — the enquiry twin of RequestDetail: basic
 * facts up top, the CONVERSATION as the main content. The chat thread
 * is the same enquiry-anchored client thread analysts see, so both
 * sides talk in one place.
 */

import { Link, useParams } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';

import { CaseSharing } from '@/components/CaseSharing';
import { ConversationView } from '@/components/Chat';
import { useClientEnquiry } from '@/hooks';

const STATUS_META: Record<string, { label: string; color: string }> = {
  submitted: { label: 'Submitted', color: 'yellow' },
  closed: { label: 'Closed', color: 'gray' },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '-';
  }
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function ClientEnquiryDetailPage() {
  const { enquiryId } = useParams<{ enquiryId: string }>();
  const { enquiry, isLoading, error } = useClientEnquiry(enquiryId);

  if (isLoading && !enquiry) {
    return (
      <Center h={400}>
        <Loader />
      </Center>
    );
  }

  if (error || !enquiry) {
    return (
      <Container size="lg" py="md">
        <Text c="red">{error?.message ?? 'Enquiry not found'}</Text>
      </Container>
    );
  }

  const status = STATUS_META[enquiry.status] ?? STATUS_META.submitted;
  const facts: string[] = [
    'Enquiry',
    ...(enquiry.requesterEmail ? [`Raised by ${enquiry.requesterEmail}`] : []),
    `Raised ${formatDate(enquiry.createdAt)}`,
  ];

  return (
    <Container size="lg" py="md">
      <Stack gap="md">
        <Anchor component={Link} to="/requests" size="sm">
          <Group gap={4} wrap="nowrap">
            <IconArrowLeft size={14} />
            All requests
          </Group>
        </Anchor>

        <Stack gap={6}>
          <Group gap="sm" wrap="wrap">
            <Title order={2}>{enquiry.codename ?? 'Enquiry'}</Title>
            <Badge size="lg" variant="light" color={status.color} data-testid="enquiry-status">
              {status.label}
            </Badge>
          </Group>
          {enquiry.subject && (
            <Text size="sm" fw={500} data-testid="enquiry-subject">
              {enquiry.subject}
            </Text>
          )}
          <Group gap={6} wrap="wrap">
            {facts.map((fact, i) => (
              <Group key={fact} gap={6} wrap="nowrap">
                {i > 0 && (
                  <Text size="sm" c="dimmed">
                    ·
                  </Text>
                )}
                <Text size="sm" c="dimmed">
                  {fact}
                </Text>
              </Group>
            ))}
          </Group>
          <CaseSharing anchor={{ kind: 'enquiry', id: enquiry.id }} />
        </Stack>

        <Card shadow="sm" padding="lg" radius="md">
          <Stack gap="sm" style={{ minHeight: '55vh', display: 'flex' }}>
            <Text size="sm" fw={600}>
              Conversation
            </Text>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: '45vh' }}>
              <ConversationView
                entityType="enquiry"
                entityId={enquiry.id}
                kind="client_thread"
                canSendPrivate={false}
              />
            </div>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
